import { Suspense, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { usePermission } from '@/features/auth/use-permission';
import {
  noteDetailOptions,
  noteLatestVersionOptions,
  notesListOptions,
} from '@/features/notes/api';
import { projectMembersOptions } from '@/features/projects/api';
import { NotesLayout } from '@/features/notes/components/NotesLayout';
import { NotesSkeleton } from '@/features/notes/components/NotesSkeleton';

const notesSearchSchema = z.object({
  // Selected page id lives in the URL so a page is shareable / bookmarkable.
  note: z.string().optional(),
  // Sidebar search query — in the URL like every other filter in the app.
  q: z.string().optional(),
});

export const Route = createFileRoute('/_authed/projects/$projectId/notes')({
  validateSearch: notesSearchSchema,
  loaderDeps: ({ search }) => ({ note: search.note }),
  loader: async ({ context: { queryClient }, params, deps }) => {
    // Fire-and-forget: the note pane suspends on its own boundary, so the
    // sidebar never waits on a page body.
    const prefetchNote = (id: string) => {
      void queryClient.prefetchQuery(noteDetailOptions(id));
      void queryClient.prefetchQuery(noteLatestVersionOptions(id));
    };
    // A deep-linked page starts loading in parallel with the list.
    if (deps.note) prefetchNote(deps.note);
    const notes = await queryClient.ensureQueryData(notesListOptions(params.projectId));
    // No `?note=` — NotesLayout falls back to the first page.
    if (!deps.note && notes[0]) prefetchNote(notes[0].id);
  },
  pendingComponent: NotesSkeleton,
  // Show the skeleton quickly on a cold load, but not on a cache hit.
  pendingMs: 100,
  pendingMinMs: 200,
  component: NotesPage,
});

function NotesPage() {
  const { projectId } = Route.useParams();
  const { note, q } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const canCreate = usePermission(projectId, 'CreateNote');
  // Resolves a note version's `edited_by` to a name. Fetched here because
  // features may not import each other.
  const { data: members } = useQuery(projectMembersOptions(projectId));
  const memberNames = useMemo(
    () => new Map((members ?? []).map((m) => [m.user_id, m.display_name])),
    [members],
  );

  const selectNote = useCallback(
    (id: string | null) =>
      void navigate({ search: (prev) => ({ ...prev, note: id ?? undefined }) }),
    [navigate],
  );

  // `replace` so typing a query doesn't stack a history entry per keystroke.
  const setSearch = useCallback(
    (next: string) =>
      void navigate({ search: (prev) => ({ ...prev, q: next || undefined }), replace: true }),
    [navigate],
  );

  return (
    <Suspense fallback={<NotesSkeleton />}>
      <NotesLayout
        projectId={projectId}
        canCreate={canCreate}
        memberNames={memberNames}
        selectedId={note}
        onSelectNote={selectNote}
        searchQuery={q ?? ''}
        onSearchChange={setSearch}
      />
    </Suspense>
  );
}
